import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PedidosForm } from "./PedidosForm";
import { Button, deleteIconButtonClass } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Download, ChevronDown, ChevronUp, Trash2, Loader2, Search, X } from "lucide-react";
import { toast } from "sonner";
import { DateInput } from "@/components/ui/date-input";
import { Label } from "@/components/ui/label";
import { MonthInput } from "@/components/ui/month-input";
import { Pagination } from "@/components/ui/pagination";
import { proveedoresAPI, ProveedorPedido, Proveedor, ProveedorPedidoProducto } from "@/lib/api";
import { openPdfTab } from "@/lib/openPdfTab";
import { pedidoProveedorLineaNetoSinIva, pedidoProveedorLineaTotal, toNum } from "@/lib/utils";
import { toMoneyNumber } from "@/lib/parseArgentineDecimal";
import { useDataRefresh, type RefreshEvent } from "@/lib/dataRefresh";
import {
  applyHistoricoMes,
  applyHistoricoRango,
  localYmd,
  type HistoricoDateFilter,
} from "../CajaBancos/cajaHistorico";
import {
  PEDIDOS_HISTORICO_PAGE_SIZE,
  applyPedidosHistoricoFecha,
  pedidosHistoricoDefaultFilter,
  pedidosHistoricoParams,
} from "./pedidosHistorico";
import { NumberField } from "@/components/ui/number-field";
import {
  comboGroupOf,
  comboVisualCodigo,
  comboVisualNombrePdf,
  comboVisualPrecioSum,
  groupComboVisualRows,
  lineNombreImpreso,
} from "@/lib/comboLine";

type ProveedoresPedidosProps = {
  /** Refetch listado del módulo padre para actualizar saldo en tarjetas sin F5 */
  onAfterPedidoSuccess?: () => void | Promise<void>;
};

function anchoNombreFlotante(texto: string, left: number): number {
  const estimado = Math.max(220, texto.trim().length * 7.2 + 28);
  const tope = Math.max(220, window.innerWidth - left - 16);
  return Math.min(estimado, tope);
}

export function ProveedoresPedidos({ onAfterPedidoSuccess }: ProveedoresPedidosProps = {}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [pedidos, setPedidos] = useState<ProveedorPedido[]>([]);
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [_loading, setLoading] = useState(true);

  const pedidosRefreshEvents = useMemo<RefreshEvent[]>(
    () => ["proveedores-pedidos"],
    [],
  );
  const bumpPedidosRefresh = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);
  useDataRefresh(pedidosRefreshEvents, bumpPedidosRefresh);

  const [tab, setTab] = useState("form");
  const [historicoFilter, setHistoricoFilter] = useState<HistoricoDateFilter>(() =>
    pedidosHistoricoDefaultFilter(localYmd()),
  );
  const [historicoProveedor, setHistoricoProveedor] = useState<string>("all");
  const [historicoApplied, setHistoricoApplied] = useState(() => ({
    filter: pedidosHistoricoDefaultFilter(localYmd()),
    proveedor: "all",
  }));
  const [historicoPage, setHistoricoPage] = useState(1);
  const [historicoPedidos, setHistoricoPedidos] = useState<ProveedorPedido[]>([]);
  const [historicoCount, setHistoricoCount] = useState(0);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const historicoRequestRef = useRef(0);

  // Filters
  const [estadoFilter, setEstadoFilter] = useState<'all' | 'pendiente'>('all');
  const [proveedorFilter, setProveedorFilter] = useState<string>('all');
  const [fechaDesde, setFechaDesde] = useState<string>('');
  const [fechaHasta, setFechaHasta] = useState<string>('');
  /** Borrador Desc. % por `pedidoId:codigo` (edición a posteriori en listado). */
  const [descuento1Draft, setDescuento1Draft] = useState<Record<string, string>>({});
  const [savingPedidoId, setSavingPedidoId] = useState<number | null>(null);
  const [editingPctPedidoId, setEditingPctPedidoId] = useState<number | null>(null);
  const pctEditCancelRef = useRef(false);
  const [editingNombreKey, setEditingNombreKey] = useState<string | null>(null);
  const nombreEditCancelRef = useRef(false);
  const [nombreFlotante, setNombreFlotante] = useState<{
    key: string;
    top: number;
    left: number;
    width: number;
    pedido: ProveedorPedido;
    row: { key: string; isCombo: boolean; comboGroup: string | null; legs: ProveedorPedidoProducto[] };
    nombre: string;
  } | null>(null);
  const [pedidoToDelete, setPedidoToDelete] = useState<ProveedorPedido | null>(null);
  const [downloadingPDF, setDownloadingPDF] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [pedidosData, proveedoresData] = await Promise.all([
          proveedoresAPI.getPedidos(undefined, { historico: 0 }),
          proveedoresAPI.list()
        ]);
        setPedidos(pedidosData || []);
        setProveedores(proveedoresData || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Error al cargar pedidos");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refresh]);

  useEffect(() => {
    if (tab !== "historico") return;
    const requestId = ++historicoRequestRef.current;
    setHistoricoLoading(true);
    proveedoresAPI
      .getPedidosHistorico(
        pedidosHistoricoParams(historicoApplied.filter, historicoApplied.proveedor, historicoPage),
      )
      .then((res) => {
        if (requestId !== historicoRequestRef.current) return;
        setHistoricoPedidos(res.results || []);
        setHistoricoCount(res.count || 0);
      })
      .catch((error) => {
        console.error("Error fetching histórico de pedidos:", error);
        if (requestId === historicoRequestRef.current) {
          toast.error("Error al cargar el histórico de pedidos");
        }
      })
      .finally(() => {
        if (requestId === historicoRequestRef.current) setHistoricoLoading(false);
      });
  }, [tab, historicoApplied, historicoPage, refresh]);

  const handleTabChange = (value: string) => {
    if (value === "historico") {
      const hoy = pedidosHistoricoDefaultFilter(localYmd());
      setHistoricoFilter(hoy);
      setHistoricoProveedor("all");
      setHistoricoApplied({ filter: hoy, proveedor: "all" });
      setHistoricoPage(1);
    }
    setTab(value);
  };

  // Helper to get provider info
  const getProveedorInfo = (proveedorId: number) => {
    return proveedores.find(p => p.id === proveedorId) || { codigo: '?', nombreRazonSocial: 'Desconocido' };
  };

  const SafePedidos = Array.isArray(pedidos) ? pedidos : [];
  const filteredPedidos = SafePedidos.filter(p => {
    const prov = getProveedorInfo(p.proveedorId);

    // Search filter
    const q = searchQuery.toLowerCase();
    const matchesProducto = (p.productos || []).some((line) =>
      String(line.codigo || "").toLowerCase().includes(q) ||
      String(line.nombre || "").toLowerCase().includes(q),
    );
    const matchesSearch = !searchQuery ||
      prov.codigo?.toLowerCase().includes(q) ||
      prov.nombreRazonSocial?.toLowerCase().includes(q) ||
      p.nroPedido.toLowerCase().includes(q) ||
      matchesProducto;

    // Estado filter
    const matchesEstado = estadoFilter === 'all' || p.estado === estadoFilter;

    // Proveedor filter
    const matchesProveedor = proveedorFilter === 'all' || prov.codigo === proveedorFilter;

    // Date range filter - if only Desde is set, filter for that specific date
    const pedidoDate = p.fechaCreacion ? new Date(p.fechaCreacion) : null;
    let matchesDate = true;
    if (fechaDesde && pedidoDate) {
      const fromDate = new Date(fechaDesde);
      const toDate = fechaHasta ? new Date(fechaHasta + 'T23:59:59') : new Date(fechaDesde + 'T23:59:59');
      matchesDate = pedidoDate >= fromDate && pedidoDate <= toDate;
    } else if (fechaHasta && pedidoDate) {
      matchesDate = pedidoDate <= new Date(fechaHasta + 'T23:59:59');
    }

    return matchesSearch && matchesEstado && matchesProveedor && matchesDate;
  });
  const tablePedidos = tab === "historico" ? historicoPedidos : filteredPedidos;

  const handlePedidoCreated = async () => {
    setRefresh((prev) => prev + 1);
    await onAfterPedidoSuccess?.();
  };

  const handleDownloadPDF = async (pedido: ProveedorPedido) => {
    if (!pedido.id) {
      toast.error("No se puede descargar: pedido sin ID");
      return;
    }
    try {
      setDownloadingPDF(pedido.id);
      const blob = await proveedoresAPI.downloadPedidoPdf(pedido.id);
      if (!blob) {
        toast.error("No se pudo generar el PDF del pedido");
        return;
      }
      const win = await openPdfTab(blob, undefined, {
        path: `/api/proveedores-pedidos/${pedido.id}/pdf/`,
      });
      if (!win) {
        toast.error("Pop-up bloqueado. Habilitá pop-ups para este sitio.");
      } else {
        toast.success("PDF del pedido abierto en nueva pestaña");
      }
    } catch (error: unknown) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message?: unknown }).message || "")
          : "";
      toast.error(message || "Error al descargar PDF del pedido");
    } finally {
      setDownloadingPDF(null);
    }
  };

  const handleDeletePedido = async (pedido: ProveedorPedido) => {
    if (!pedido.id) {
      toast.error("ID de pedido no válido");
      return;
    }

    try {
      // Check if related Remito is finalized - if so, prevent deletion
      const remitos = await proveedoresAPI.getRemitos({ pedido__numero_pedido: pedido.nroPedido });
      const remitoFinalizado = remitos.find(r => r.estado === 'finalizado');

      if (remitoFinalizado) {
        toast.error(`No se puede eliminar: El Remito ${remitoFinalizado.nroRemito || remitoFinalizado.id} ya está finalizado.`);
        return;
      }

      setPedidoToDelete(pedido);
    } catch (error) {
      console.error("Error deleting pedido:", error);
      toast.error("Error al eliminar el pedido");
    }
  };

  const handleConfirmDeletePedido = async () => {
    const pedido = pedidoToDelete;
    if (!pedido?.id) {
      toast.error("ID de pedido no válido");
      return;
    }

    try {
      await proveedoresAPI.deletePedido(pedido.id);
      toast.success(`Pedido ${pedido.nroPedido} eliminado correctamente`);
      setPedidoToDelete(null);
      setRefresh(prev => prev + 1);
      await onAfterPedidoSuccess?.();
    } catch (error) {
      console.error("Error deleting pedido:", error);
      toast.error("Error al eliminar el pedido");
    }
  };

  const toggleRow = (nroPedido: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(nroPedido)) {
      newExpanded.delete(nroPedido);
    } else {
      newExpanded.add(nroPedido);
    }
    setExpandedRows(newExpanded);
  };

  const precioUnitProducto = (producto: ProveedorPedidoProducto) =>
    toNum(producto.precioBase ?? producto.precio_base);

  const desc1Producto = (producto: ProveedorPedidoProducto) =>
    toNum(producto.descuento1 ?? producto.descuento_1);

  const desc2Producto = (producto: ProveedorPedidoProducto) =>
    toNum(producto.descuento2 ?? producto.descuento_2);

  const draftKey = (pedidoId: number | undefined, codigo: string) =>
    `${pedidoId ?? "x"}:${codigo}`;

  const parsePct = (raw: string): number | null => {
    const t = String(raw).trim().replace(",", ".");
    if (t === "") return 0;
    const n = parseFloat(t);
    return Number.isNaN(n) ? null : n;
  };

  const effectiveDesc1 = (pedido: ProveedorPedido, producto: ProveedorPedidoProducto) => {
    const draft = descuento1Draft[draftKey(pedido.id, producto.codigo)];
    if (draft !== undefined) {
      const n = parsePct(draft);
      if (n !== null) return Math.min(100, Math.max(0, n));
    }
    return desc1Producto(producto);
  };

  const lineOptsProducto = (
    producto: ProveedorPedidoProducto,
    d1: number,
  ) => ({
    precioBase: precioUnitProducto(producto),
    cantidad: toNum(producto.cantidad),
    descuento1: d1,
    descuento2: desc2Producto(producto),
  });

  const calcularTotalProducto = (pedido: ProveedorPedido, producto: ProveedorPedidoProducto) =>
    pedidoProveedorLineaTotal(lineOptsProducto(producto, effectiveDesc1(pedido, producto)));

  const brutoLineaPedido = (producto: ProveedorPedidoProducto) =>
    precioUnitProducto(producto) * toNum(producto.cantidad);

  const importeDescuentoLineaPedido = (pedido: ProveedorPedido, producto: ProveedorPedidoProducto) =>
    brutoLineaPedido(producto) - pedidoProveedorLineaNetoSinIva(
      lineOptsProducto(producto, effectiveDesc1(pedido, producto)),
    );

  const formatMoneyPed = (n: number) =>
    n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const importePedidoMostrado = (pedido: ProveedorPedido) => {
    const hasDraft = (pedido.productos || []).some(
      (p) => descuento1Draft[draftKey(pedido.id, p.codigo)] !== undefined,
    );
    if (!hasDraft) return toNum(pedido.importe);
    return (pedido.productos || []).reduce(
      (sum, p) => sum + calcularTotalProducto(pedido, p),
      0,
    );
  };

  const pedidoDescEditable = (pedido: ProveedorPedido) => pedido.estado === "pendiente";

  const nombreImpresoFila = (row: { isCombo: boolean; legs: ProveedorPedidoProducto[] }) =>
    row.isCombo ? comboVisualNombrePdf(row.legs) : lineNombreImpreso(row.legs[0]);

  const replacePedido = (updated: ProveedorPedido) => {
    setPedidos((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    setHistoricoPedidos((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
  };

  const finishNombreEdit = async (
    pedido: ProveedorPedido,
    row: { key: string; isCombo: boolean; comboGroup: string | null; legs: ProveedorPedidoProducto[] },
    raw: string,
  ) => {
    if (nombreEditCancelRef.current) {
      nombreEditCancelRef.current = false;
      return;
    }
    const editKey = pedido.id != null ? `${pedido.id}:${row.key}` : "";
    setEditingNombreKey((cur) => (cur === editKey ? null : cur));
    const nombre = raw.trim();
    if (!nombre) {
      toast.error("El nombre no puede estar vacío");
      return;
    }
    if (nombre === nombreImpresoFila(row)) return;
    if (!pedido.id) {
      toast.error("ID de pedido no válido");
      return;
    }
    try {
      setSavingPedidoId(pedido.id);
      const updated = await proveedoresAPI.updatePedidoItemNombre(pedido.id, {
        codigo: row.legs[0].codigo,
        ...(row.isCombo && row.comboGroup ? { comboGroup: row.comboGroup } : {}),
        nombreProducto: nombre,
      });
      replacePedido(updated);
      toast.success("Nombre del PDF actualizado");
    } catch (error: unknown) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message?: unknown }).message || "")
          : "";
      toast.error(message || "Error al actualizar el nombre");
    } finally {
      setSavingPedidoId(null);
    }
  };

  const commitListadoDescuento1 = async (
    pedido: ProveedorPedido,
    codigo: string,
    group?: string | null,
  ) => {
    const key = draftKey(pedido.id, group || codigo);
    const draft = descuento1Draft[key];
    if (draft === undefined) return;
    const n = parsePct(draft);
    const val = n === null ? 0 : Math.min(100, Math.max(0, n));
    const line = pedido.productos?.find((p) =>
      group ? comboGroupOf(p) === group : p.codigo === codigo,
    );
    if (!line) return;
    if (val === desc1Producto(line)) {
      setDescuento1Draft((d) => {
        const next = { ...d };
        delete next[key];
        return next;
      });
      return;
    }
    if (!pedido.id) {
      toast.error("ID de pedido no válido");
      return;
    }
    try {
      setSavingPedidoId(pedido.id);
      const productos = (pedido.productos || []).map((p) => ({
        codigo: p.codigo,
        nombre: p.nombre,
        cantidad: p.cantidad,
        precioBase: precioUnitProducto(p),
        descuento1: (group ? comboGroupOf(p) === group : p.codigo === codigo)
          ? val
          : desc1Producto(p),
        descuento2: desc2Producto(p),
        recibido: p.recibido,
        ...(comboGroupOf(p) ? { comboGroup: comboGroupOf(p) } : {}),
        ...((p.nombrePdf || p.nombre_pdf)
          ? { nombrePdf: p.nombrePdf || p.nombre_pdf }
          : {}),
        ...((p.comboNombrePdf || p.combo_nombre_pdf)
          ? { comboNombrePdf: p.comboNombrePdf || p.combo_nombre_pdf }
          : {}),
      }));
      const updated = await proveedoresAPI.updatePedido(
        getProveedorInfo(pedido.proveedorId).codigo,
        pedido.id,
        { productos },
      );
      setPedidos((prev) => prev.map((p) => (p.id === pedido.id ? { ...p, ...updated } : p)));
      setDescuento1Draft((d) => {
        const next = { ...d };
        delete next[key];
        return next;
      });
      toast.success("Descuento actualizado");
      await onAfterPedidoSuccess?.();
    } catch (error: unknown) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message?: unknown }).message || "")
          : "";
      toast.error(message || "Error al actualizar el descuento");
    } finally {
      setSavingPedidoId(null);
    }
  };

  const pctCabeceraLabel = (pedido: ProveedorPedido) => {
    const n = toNum(pedido.descuentoTotal);
    if (!Number.isFinite(n) || n <= 0) return "0";
    return n.toLocaleString("es-AR", { maximumFractionDigits: 2 });
  };

  const pctEscritoLabel = (raw: string) => {
    const n = parsePct(raw);
    if (n === null || n <= 0) return "0";
    return Math.min(100, Math.max(0, n)).toLocaleString("es-AR", { maximumFractionDigits: 2 });
  };

  const finishPctEdit = (pedido: ProveedorPedido, raw: string) => {
    if (pctEditCancelRef.current) {
      pctEditCancelRef.current = false;
      setEditingPctPedidoId((current) => (current === pedido.id ? null : current));
      return;
    }
    setEditingPctPedidoId((current) => (current === pedido.id ? null : current));
    if (pctCabeceraLabel(pedido) === pctEscritoLabel(raw)) return;
    void commitDescuentoCabecera(pedido, raw);
  };

  const commitDescuentoCabecera = async (pedido: ProveedorPedido, raw: string) => {
    if (!pedidoDescEditable(pedido) || !pedido.id) return;
    const n = parsePct(raw);
    const val = n === null ? 0 : Math.min(100, Math.max(0, n));
    if (Math.abs(val - toNum(pedido.descuentoTotal)) < 1e-6) return;
    try {
      setSavingPedidoId(pedido.id);
      const updated = await proveedoresAPI.updatePedido(
        getProveedorInfo(pedido.proveedorId).codigo,
        pedido.id,
        { descuentoTotal: val },
      );
      setPedidos((prev) => prev.map((p) => (p.id === pedido.id ? { ...p, ...updated } : p)));
      toast.success("Descuento del total actualizado");
      await onAfterPedidoSuccess?.();
    } catch (error: unknown) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message?: unknown }).message || "")
          : "";
      toast.error(message || "Error al actualizar el descuento del total");
    } finally {
      setSavingPedidoId(null);
    }
  };

  const commitImporteCabecera = async (pedido: ProveedorPedido, raw: string) => {
    if (!pedidoDescEditable(pedido) || !pedido.id) return;
    const parsed = toMoneyNumber(raw);
    if (parsed === null || !Number.isFinite(parsed) || parsed < 0) return;
    if (Math.abs(parsed - toNum(pedido.importe)) < 0.5) return;
    try {
      setSavingPedidoId(pedido.id);
      const updated = await proveedoresAPI.updatePedido(
        getProveedorInfo(pedido.proveedorId).codigo,
        pedido.id,
        { importe: parsed },
      );
      setPedidos((prev) => prev.map((p) => (p.id === pedido.id ? { ...p, ...updated } : p)));
      toast.success("Importe actualizado");
      await onAfterPedidoSuccess?.();
    } catch (error: unknown) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message?: unknown }).message || "")
          : "";
      toast.error(message || "Error al actualizar el importe");
    } finally {
      setSavingPedidoId(null);
    }
  };

  return (
    <>
      <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="form">Nuevo Pedido</TabsTrigger>
        <TabsTrigger value="listado">Listado</TabsTrigger>
        <TabsTrigger value="historico">Histórico</TabsTrigger>
      </TabsList>

      <TabsContent value="form">
        <PedidosForm onPedidoCreated={handlePedidoCreated} />
      </TabsContent>

      <TabsContent value="listado">
        <div className="space-y-4 mt-4">
          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Buscar</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Código, nombre o nro pedido..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                />
                {(searchQuery || estadoFilter !== "all" || proveedorFilter !== "all" || fechaDesde || fechaHasta) ? (
                  <button
                    type="button"
                    aria-label="Limpiar filtros"
                    title="Limpiar filtros"
                    onClick={() => {
                      setSearchQuery("");
                      setEstadoFilter("all");
                      setProveedorFilter("all");
                      setFechaDesde("");
                      setFechaHasta("");
                    }}
                    className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-foreground hover:bg-muted"
                  >
                    <X className="h-4 w-4" strokeWidth={2.5} />
                  </button>
                ) : null}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estado</label>
              <select
                value={estadoFilter}
                onChange={(e) => setEstadoFilter(e.target.value as 'all' | 'pendiente')}
                className="w-full px-3 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              >
                <option value="all">Todos</option>
                <option value="pendiente">Pendiente</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Proveedor</label>
              <select
                value={proveedorFilter}
                onChange={(e) => setProveedorFilter(e.target.value)}
                className="w-full px-3 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              >
                <option value="all">Todos</option>
                {proveedores.map(p => (
                  <option key={p.codigo} value={p.codigo}>{p.codigo} - {p.nombreRazonSocial}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Desde</label>
              <DateInput
                value={fechaDesde}
                onChange={(ymd) => setFechaDesde(ymd)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Hasta</label>
              <DateInput
                value={fechaHasta}
                onChange={(ymd) => setFechaHasta(ymd)}
              />
            </div>
          </div>

        </div>
      </TabsContent>

      <TabsContent value="historico">
        <form
          className="flex flex-wrap gap-4 items-end mt-4"
          onSubmit={(event) => {
            event.preventDefault();
            setHistoricoApplied({ filter: historicoFilter, proveedor: historicoProveedor });
            setHistoricoPage(1);
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="pedidos-historico-fecha">Fecha</Label>
            <DateInput
              id="pedidos-historico-fecha"
              value={historicoFilter.fecha}
              onChange={(ymd) => setHistoricoFilter((prev) => applyPedidosHistoricoFecha(prev, ymd))}
              className="w-[200px]"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pedidos-historico-desde">Desde</Label>
            <DateInput
              id="pedidos-historico-desde"
              value={historicoFilter.desde}
              onChange={(ymd) => setHistoricoFilter((prev) => applyHistoricoRango(ymd, prev.hasta))}
              className="w-[200px]"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pedidos-historico-hasta">Hasta</Label>
            <DateInput
              id="pedidos-historico-hasta"
              value={historicoFilter.hasta}
              onChange={(ymd) => setHistoricoFilter((prev) => applyHistoricoRango(prev.desde, ymd))}
              className="w-[200px]"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pedidos-historico-mes">Mes</Label>
            <MonthInput
              id="pedidos-historico-mes"
              value={historicoFilter.mes}
              onChange={(mes) => setHistoricoFilter(applyHistoricoMes(mes))}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pedidos-historico-proveedor">Proveedor</Label>
            <select
              id="pedidos-historico-proveedor"
              value={historicoProveedor}
              onChange={(e) => setHistoricoProveedor(e.target.value)}
              className="h-10 w-[240px] px-3 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
            >
              <option value="all">Todos</option>
              {proveedores.map(p => (
                <option key={p.codigo} value={p.codigo}>{p.codigo} - {p.nombreRazonSocial}</option>
              ))}
            </select>
          </div>
          <Button type="submit" size="sm" className="flex items-center gap-1">
            <Search className="h-4 w-4" />
            Buscar
          </Button>
        </form>
      </TabsContent>

      {(tab === "listado" || tab === "historico") && (
        <div className="space-y-4 mt-4">
          {tab === "historico" && historicoLoading && historicoPedidos.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : tab === "listado" && !searchQuery && pedidos.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No hay pedidos en curso. Los recibidos, completos o cancelados están en Histórico.
            </div>
          ) : tablePedidos.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              {tab === "historico"
                ? "No hay pedidos recibidos para ese filtro. Cambiá la fecha, el rango o el mes (o borrá la fecha para ver todos)."
                : "No se encontraron resultados"}
            </div>
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm min-w-[600px]">
                <thead className="bg-muted">
                  <tr>
                    <th className="w-8"></th>
                    <th className="px-2 md:px-4 py-2 text-left">Código</th>
                    <th className="px-2 md:px-4 py-2 text-left">Proveedor</th>
                    <th className="px-2 md:px-4 py-2 text-left">Nro Pedido</th>
                    <th className="px-2 md:px-4 py-2 text-left hidden lg:table-cell">Factura Nro</th>
                    <th className="px-2 md:px-4 py-2 text-left">Estado</th>
                    <th className="px-2 md:px-4 py-2 text-left hidden md:table-cell">Fecha Creación</th>
                    <th className="px-2 md:px-4 py-2 text-right hidden md:table-cell">Desc. total %</th>
                    <th className="px-2 md:px-4 py-2 text-left hidden lg:table-cell">Fecha Finalizado</th>
                    <th className="px-2 md:px-4 py-2 text-right">Importe</th>
                    <th className="px-2 md:px-4 py-2 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {tablePedidos.map((pedido) => (
                    <React.Fragment key={pedido.nroPedido}>
                      <tr className="border-b hover:bg-muted/50">
                        <td className="py-3 px-2">
                          <button
                            onClick={() => toggleRow(pedido.nroPedido)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            {expandedRows.has(pedido.nroPedido) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                        </td>
                        <td className="px-2 md:px-4 py-2">{getProveedorInfo(pedido.proveedorId).codigo || '-'}</td>
                        <td className="px-2 md:px-4 py-2 truncate max-w-[150px]">{getProveedorInfo(pedido.proveedorId).nombreRazonSocial}</td>
                        <td className="px-2 md:px-4 py-2">{pedido.nroPedido}</td>
                        <td className="px-2 md:px-4 py-2 hidden lg:table-cell">{pedido.facturaNro || '-'}</td>
                        <td className="px-2 md:px-4 py-2">
                          <span className={`px-2 py-1 rounded text-xs ${pedido.estado === 'recibido' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {pedido.estado}
                          </span>
                        </td>
                        <td className="px-2 md:px-4 py-2 hidden md:table-cell">{pedido.fechaCreacion}</td>
                        <td className="px-2 md:px-4 py-2 text-right hidden md:table-cell">
                          {pedidoDescEditable(pedido) && editingPctPedidoId === pedido.id ? (
                            <input
                              autoFocus
                              key={`${pedido.id}-pct-edit`}
                              defaultValue={pctCabeceraLabel(pedido)}
                              disabled={savingPedidoId === pedido.id}
                              onBlur={(e) => finishPctEdit(pedido, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  (e.currentTarget as HTMLInputElement).blur();
                                }
                                if (e.key === "Escape") {
                                  e.preventDefault();
                                  pctEditCancelRef.current = true;
                                  setEditingPctPedidoId(null);
                                }
                              }}
                              className="w-20 px-2 py-1 border border-input bg-background rounded text-sm text-right tabular-nums"
                              inputMode="decimal"
                              aria-label="Descuento al total"
                            />
                          ) : pedidoDescEditable(pedido) ? (
                            <button
                              type="button"
                              className="w-20 text-right tabular-nums hover:underline disabled:opacity-50"
                              disabled={savingPedidoId === pedido.id}
                              onClick={() => pedido.id && setEditingPctPedidoId(pedido.id)}
                              aria-label="Editar descuento al total"
                            >
                              {pctCabeceraLabel(pedido)}
                            </button>
                          ) : (
                            pctCabeceraLabel(pedido)
                          )}
                        </td>
                        <td className="px-2 md:px-4 py-2 hidden lg:table-cell">{pedido.fechaFinalizado || '-'}</td>
                        <td className="px-2 md:px-4 py-2 text-right">
                          {pedidoDescEditable(pedido) ? (
                            <input
                              key={`${pedido.id}-imp-${pedido.importe ?? 0}`}
                              defaultValue={formatMoneyPed(toNum(pedido.importe))}
                              disabled={savingPedidoId === pedido.id}
                              onBlur={(e) => commitImporteCabecera(pedido, e.target.value)}
                              className="w-28 px-2 py-1 border border-input bg-background rounded text-sm text-right tabular-nums"
                              inputMode="decimal"
                              aria-label="Importe del pedido"
                            />
                          ) : (
                            <>${formatMoneyPed(importePedidoMostrado(pedido))}</>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={downloadingPDF === pedido.id}
                              onClick={() => handleDownloadPDF(pedido)}
                              title="Descargar PDF"
                            >
                              {downloadingPDF === pedido.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </Button>
                            {tab !== "historico" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeletePedido(pedido)}
                                title="Eliminar Pedido"
                                className={deleteIconButtonClass}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedRows.has(pedido.nroPedido) && (
                        <tr>
                          <td colSpan={11} className="bg-muted/30 p-4">
                            <div className="space-y-2">
                              <h4 className="font-semibold text-sm mb-2">Productos:</h4>
                              <div className="border border-border rounded-lg overflow-hidden">
                                <table className="w-full">
                                  <thead className="bg-muted">
                                    <tr>
                                      <th className="text-center py-2 px-4 text-xs font-medium w-16">Recibido</th>
                                      <th className="text-left py-2 px-4 text-xs font-medium">Código</th>
                                      <th className="text-left py-2 px-4 text-xs font-medium">Producto</th>
                                      <th className="text-center py-2 px-4 text-xs font-medium">Cant.</th>
                                      <th className="text-center py-2 px-4 text-xs font-medium">Desc. %</th>
                                      <th className="text-right py-2 px-4 text-xs font-medium">Desc. $</th>
                                      <th className="text-right py-2 px-4 text-xs font-medium">Precio Base</th>
                                      <th className="text-right py-2 px-4 text-xs font-medium">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {groupComboVisualRows(pedido.productos || []).map((row) => {
                                      const producto = row.legs[0];
                                      const codigo = row.isCombo ? comboVisualCodigo(row.legs) : producto.codigo;
                                      const nombre = nombreImpresoFila(row);
                                      const nombreKey = pedido.id != null ? `${pedido.id}:${row.key}` : "";
                                      const precio = row.isCombo
                                        ? comboVisualPrecioSum(row.legs.map((leg) => ({
                                          ...leg,
                                          precioBase: precioUnitProducto(leg),
                                        })))
                                        : precioUnitProducto(producto);
                                      const descKey = draftKey(pedido.id, row.comboGroup || producto.codigo);
                                      const descPesos = row.legs.reduce(
                                        (s, leg) => s + importeDescuentoLineaPedido(pedido, leg),
                                        0,
                                      );
                                      const totalLinea = row.legs.reduce(
                                        (s, leg) => s + calcularTotalProducto(pedido, leg),
                                        0,
                                      );
                                      const recibido = row.legs.every((leg) => leg.recibido);
                                      return (
                                      <tr key={row.key} className="border-t border-border">
                                        <td className="py-2 px-4 text-center">
                                          <span className={`inline-flex items-center justify-center w-5 h-5 rounded ${recibido ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-500'}`}>
                                            {recibido ? '✓' : '✗'}
                                          </span>
                                        </td>
                                        <td className="py-2 px-4 text-xs whitespace-pre-line">{codigo}</td>
                                        <td className="py-2 px-4 text-xs whitespace-normal break-words">
                                          {pedidoDescEditable(pedido) ? (
                                            <button
                                              type="button"
                                              className="text-left whitespace-normal break-words hover:underline disabled:opacity-50"
                                              disabled={savingPedidoId === pedido.id}
                                              title="Este nombre se imprime en el PDF. El artículo no cambia."
                                              onClick={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setEditingNombreKey(nombreKey);
                                                setNombreFlotante({
                                                  key: nombreKey,
                                                  top: rect.bottom + 4,
                                                  left: rect.left,
                                                  width: anchoNombreFlotante(nombre, rect.left),
                                                  pedido,
                                                  row,
                                                  nombre,
                                                });
                                              }}
                                            >
                                              {nombre}
                                            </button>
                                          ) : (
                                            nombre
                                          )}
                                        </td>
                                        <td className="py-2 px-4 text-xs text-center">{producto.cantidad}</td>
                                        <td className="py-2 px-4 text-xs text-center">
                                          {pedidoDescEditable(pedido) ? (
                                            <NumberField
                                              min={0}
                                              max={100}
                                              step={0.5}
                                              disabled={savingPedidoId === pedido.id}
                                              title="El proveedor informa el descuento después"
                                              value={
                                                descuento1Draft[descKey] !== undefined
                                                  ? descuento1Draft[descKey]
                                                  : desc1Producto(producto) === 0
                                                    ? ""
                                                    : String(desc1Producto(producto))
                                              }
                                              onFocus={() => {
                                                setDescuento1Draft((d) => {
                                                  if (d[descKey] !== undefined) return d;
                                                  const show =
                                                    desc1Producto(producto) === 0
                                                      ? ""
                                                      : String(desc1Producto(producto));
                                                  return { ...d, [descKey]: show };
                                                });
                                              }}
                                              onChange={(e) =>
                                                setDescuento1Draft((d) => ({
                                                  ...d,
                                                  [descKey]: e.target.value,
                                                }))
                                              }
                                              onBlur={() => void commitListadoDescuento1(pedido, producto.codigo, row.comboGroup)}
                                              onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                  e.preventDefault();
                                                  (e.currentTarget as HTMLInputElement).blur();
                                                }
                                              }}
                                              className="w-[4.25rem] px-2 py-1 border border-input bg-background rounded text-xs text-center"
                                            />
                                          ) : (
                                            <>{desc1Producto(producto) || 0}%</>
                                          )}
                                        </td>
                                        <td className="py-2 px-4 text-xs text-right text-muted-foreground tabular-nums">
                                          ${formatMoneyPed(descPesos)}
                                        </td>
                                        <td className="py-2 px-4 text-xs text-right">
                                          ${precio.toLocaleString('es-AR')}
                                        </td>
                                        <td className="py-2 px-4 text-xs text-right font-medium tabular-nums">
                                          ${formatMoneyPed(totalLinea)}
                                        </td>
                                      </tr>
                                      );
                                    })}
                                    {!pedido.productos && (
                                      <tr><td colSpan={8} className="text-center py-2">No hay detalles de productos</td></tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {tab === "historico" && historicoCount > 0 && (
            <Pagination
              currentPage={historicoPage}
              totalCount={historicoCount}
              pageSize={PEDIDOS_HISTORICO_PAGE_SIZE}
              onPageChange={setHistoricoPage}
            />
          )}
        </div>
      )}
      </Tabs>

      {nombreFlotante ? (
        <input
          autoFocus
          key={nombreFlotante.key}
          defaultValue={nombreFlotante.nombre}
          disabled={savingPedidoId === nombreFlotante.pedido.id}
          title="Este nombre se imprime en el PDF. El artículo no cambia."
          aria-label="Nombre para el PDF"
          style={{
            position: "fixed",
            top: nombreFlotante.top,
            left: nombreFlotante.left,
            width: nombreFlotante.width,
            zIndex: 40,
          }}
          onBlur={(e) => {
            const actual = nombreFlotante;
            setNombreFlotante(null);
            void finishNombreEdit(actual.pedido, actual.row, e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.currentTarget as HTMLInputElement).blur();
            }
            if (e.key === "Escape") {
              e.preventDefault();
              nombreEditCancelRef.current = true;
              setEditingNombreKey(null);
              setNombreFlotante(null);
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
          className="px-2 py-1 border border-input bg-background rounded text-xs shadow-none"
        />
      ) : null}

      <AlertDialog open={!!pedidoToDelete} onOpenChange={(open) => !open && setPedidoToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar pedido?</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro que desea eliminar el Pedido {pedidoToDelete?.nroPedido}? Esto también eliminará el Remito asociado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => void handleConfirmDeletePedido()}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}